import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { LogIn, Menu, X, Info, Sun, Moon, Phone, User } from 'lucide-react';
import { useSignIn, useUser, useClerk } from '@clerk/clerk-react';

// Login Modal Component - powered by Clerk
export const LoginModal = ({ theme, isDarkMode, onClose, onSignInSuccess, onSignOutSuccess, isVisible = true }) => {
  const { signIn, isLoaded, setActive } = useSignIn();
  const { user } = useUser();
  const { signOut } = useClerk();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoaded) return;
    setLoading(true);
    setError('');
    try {
      const result = await signIn.create({ identifier: email, password });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        onClose();
        if (onSignInSuccess) onSignInSuccess();
      } else if (result.status === 'needs_client_trust') {
        const emailFactor = result.supportedFirstFactors?.find(f => f.strategy === 'email_code');
        if (emailFactor) {
          await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: emailFactor.emailAddressId });
        }
        setVerifying(true);
      } else {
        setError(`Unexpected status: ${result.status}`);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!isLoaded) return;
    setLoading(true);
    setError('');
    try {
      const result = await signIn.attemptFirstFactor({ strategy: 'email_code', code });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        onClose();
        if (onSignInSuccess) onSignInSuccess();
      } else {
        setError('Verification failed, please try again.');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center" style={{zIndex: 9999, opacity: isVisible ? 1 : 0, transition: 'opacity 0.3s ease'}}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" style={{zIndex: 0}} />
        <div className={`relative w-full max-w-sm mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700/50' : 'bg-white border-gray-200'} border rounded-2xl p-8 shadow-2xl`} style={{zIndex: 1, transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(10px)', transition: 'transform 0.3s ease'}}>
          <button onClick={onClose} className={`absolute top-4 right-4 ${theme.text.muted} hover:text-pink-400 transition-colors`}><X size={20} /></button>
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl mx-auto mb-4 flex items-center justify-center">
              <LogIn size={22} className="text-white" />
            </div>
            <h2 className={`text-xl font-bold ${theme.text.primary} font-sans`}>Check your email</h2>
            <p className={`text-xs ${theme.text.muted} font-sans mt-1`}>We sent a verification code to {email}</p>
          </div>
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className={`text-xs font-medium ${theme.text.secondary} font-sans block mb-1`}>Verification Code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter code"
                className={`w-full ${theme.input} rounded-lg px-4 py-3 text-sm font-sans text-center tracking-widest focus:ring-2 focus:ring-pink-500/30 outline-none transition-all`}
                required
              />
            </div>
            {error && <p className="text-red-400 text-xs font-sans text-center">{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-400 hover:to-pink-500 disabled:opacity-60 text-white py-3 rounded-lg font-medium text-sm font-sans transition-all duration-300 flex items-center justify-center gap-2"
            >
              {loading ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> : 'Verify'}
            </button>
          </form>
        </div>
      </div>,
      document.body
    );
  }

  const getErrorMessage = (err) => {
    const code = err.errors?.[0]?.code;
    const msg = err.errors?.[0]?.longMessage || err.errors?.[0]?.message || '';
    if (code === 'form_identifier_not_found' || msg.includes('identifier')) return "We don't recognise that email address. Please check and try again.";
    if (code === 'form_password_incorrect' || msg.includes('password')) return 'Incorrect password. Please try again.';
    if (code === 'too_many_requests') return 'Too many attempts. Please wait a moment and try again.';
    if (code === 'form_identifier_exists') return 'An account with this email already exists.';
    if (msg.includes('breached') || msg.includes('data breach')) return 'This password has been found in a data breach. Please reset your password.';
    return msg || 'Something went wrong. Please try again.';
  };

  return (
    <>
      {ReactDOM.createPortal(
      <div className="fixed inset-0 flex items-center justify-center" style={{zIndex: 9999, opacity: isVisible ? 1 : 0, transition: 'opacity 0.3s ease'}}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" style={{zIndex: 0}} />
        <div
          className={`relative w-full max-w-sm mx-4 ${isDarkMode ? 'bg-slate-800 border-slate-700/50' : 'bg-white border-gray-200'} border rounded-2xl p-8 shadow-2xl`}
          style={{zIndex: 1, transform: isVisible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(10px)', transition: 'transform 0.3s ease'}}
        >
        <button onClick={onClose} className={`absolute top-4 right-4 ${theme.text.muted} hover:text-pink-400 transition-colors`}>
          <X size={20} />
        </button>

        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center">
            <img src="/sc_logo_notext.svg" alt="Spiral Creations" className="w-12 h-12 object-contain" />
          </div>
          <h2 className={`text-xl font-bold ${theme.text.primary} font-sans`}>Client Login</h2>
          <p className={`text-xs ${theme.text.muted} font-sans mt-1`}>Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`text-xs font-medium ${theme.text.secondary} font-sans block mb-1`}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={`w-full ${theme.input} rounded-lg px-4 py-3 text-sm font-sans focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500 outline-none transition-all`}
              required
            />
          </div>
          <div>
            <label className={`text-xs font-medium ${theme.text.secondary} font-sans block mb-1`}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={`w-full ${theme.input} rounded-lg px-4 py-3 text-sm font-sans focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500 outline-none transition-all`}
              required
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
              <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"/></svg>
              <p className="text-red-400 text-xs font-sans">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !isLoaded}
            className="w-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-400 hover:to-pink-500 disabled:opacity-60 text-white py-3 rounded-lg font-medium text-sm font-sans transition-all duration-300 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Signing in…
              </>
            ) : (
              <>
                <LogIn size={16} />
                Sign In
              </>
            )}
          </button>
        </form>

        <p className={`text-center text-xs ${theme.text.muted} font-sans mt-4`}>
          Need access?{' '}
          <button
            onClick={() => { onClose(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200); }}
            className="text-pink-400 hover:text-pink-300 transition-colors"
          >
            Contact us
          </button>
        </p>
      </div>
    </div>,
    document.body
    )}
    </>
  );
};

// Top Status Bar Component
export const TopStatusBar = ({ 
  theme, 
  isDarkMode, 
  serverStatus, 
  servicesAvailability,
  handleServicesToggle, 
  handleMetricsToggle,
  onLoginClick,
  onContactClick
}) => {
  const { user, isLoaded } = useUser();
  return (
    <div className={`border-b ${theme.topBar} px-6 py-2 relative`} style={{
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      zIndex: 100
    }}>
      <div className="flex items-center justify-between max-w-6xl mx-auto relative z-10">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1.5">
<div style={{position:'relative',width:'20px',height:'20px',flexShrink:0}}>
              {serverStatus.isOnline && (<>
                <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:serverStatus.isDegraded?'#fbbf24':'#4ade80',animation:'pulseRing 2s ease-out infinite',opacity:0}} />
                <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:serverStatus.isDegraded?'#fbbf24':'#4ade80',animation:'pulseRing 2s ease-out infinite',animationDelay:'1s',opacity:0}} />
              </>)}
              <div style={{position:'absolute',top:'6px',left:'6px',width:'8px',height:'8px',borderRadius:'50%',backgroundColor:serverStatus.isOnline?(serverStatus.isDegraded?'#fbbf24':'#4ade80'):'#f87171'}} />
            </div>
            <button 
              onClick={handleServicesToggle}
              className={`services-button text-xs ${theme.text.secondary} font-sans cursor-pointer flex items-center gap-1`}
            >
              <span className="underline decoration-dotted underline-offset-2 whitespace-nowrap" style={{ textDecorationColor: isDarkMode ? '#9ca3af' : '#6b7280' }}><span className="hidden sm:inline">Service </span>{serverStatus.status}{servicesAvailability ? `: ${servicesAvailability}` : ''}</span>
              <Info size={12} className={theme.text.secondary} />
            </button>
          </div>
          <div className={`flex items-center gap-1.5 text-xs ${theme.text.secondary} font-sans`}>
            <canvas
              width="36" height="24"
              style={{width:'18px',height:'12px',flexShrink:0}}
              ref={el => {
                if (!el || el._hbInit) return;
                el._hbInit = true;
                const ctx = el.getContext('2d');
                const pts = [[0,6],[3,6],[4.5,1],[6,11],[7.5,3],[9,9],[10.5,6],[18,6]];
                const scale = 2;
                const path = [];
                for (let i = 0; i < pts.length - 1; i++) {
                  const [x0,y0] = pts[i], [x1,y1] = pts[i+1];
                  const dx = x1-x0, dy = y1-y0;
                  const steps = Math.ceil(Math.hypot(dx,dy)/0.1);
                  for (let s = 0; s < steps; s++) path.push([x0+dx*s/steps, y0+dy*s/steps]);
                }
                path.push(pts[pts.length-1]);
                const total = path.length;
                const trailSize = Math.floor(total * 0.18);
                const baseColor = isDarkMode ? '#475569' : '#d1d5db';
                let start = null;
                const duration = 3000;
                function draw(ts) {
                  if (!start) start = ts;
                  const t = ((ts - start) % duration) / duration;
                  ctx.clearRect(0, 0, el.width, el.height);
                  ctx.beginPath();
                  ctx.strokeStyle = baseColor;
                  ctx.lineWidth = 1.5 * scale;
                  ctx.lineCap = 'round';
                  ctx.lineJoin = 'round';
                  pts.forEach(([x,y],i) => i===0 ? ctx.moveTo(x*scale,y*scale) : ctx.lineTo(x*scale,y*scale));
                  ctx.stroke();
                  const head = Math.floor(t * (total + trailSize));
                  const tail = Math.max(0, head - trailSize);
                  const drawEnd = Math.min(head, total - 1);
                  const drawStart = Math.min(tail, total - 1);
                  if (drawStart < drawEnd) {
                    for (let i = drawStart; i < drawEnd; i++) {
                      const progress = (i - tail) / trailSize;
                      let alpha = 1;
                      if (progress < 0.15) alpha = progress / 0.15;
                      const exitedFraction = Math.max(0, head - total) / trailSize;
                      alpha *= (1 - exitedFraction);
                      ctx.beginPath();
                      ctx.strokeStyle = `rgba(235,0,139,${Math.max(0, Math.min(1, alpha))})`;
                      ctx.lineWidth = 1.5 * scale;
                      ctx.lineCap = 'round';
                      ctx.lineJoin = 'round';
                      ctx.moveTo(path[i][0]*scale, path[i][1]*scale);
                      ctx.lineTo(path[i+1][0]*scale, path[i+1][1]*scale);
                      ctx.stroke();
                    }
                  }
                  requestAnimationFrame(draw);
                }
                requestAnimationFrame(draw);
              }}
            />
            <button 
              onClick={handleMetricsToggle}
              className={`uptime-button ${theme.text.secondary} font-sans cursor-pointer underline decoration-dotted underline-offset-2 flex items-center gap-1`}
              style={{ textDecorationColor: isDarkMode ? '#9ca3af' : '#6b7280' }}
            >
              <span className="hidden sm:inline">{serverStatus.uptime ? `Server Uptime: ${serverStatus.uptime}` : 'Server Uptime'}</span>
              <span className="sm:hidden">{serverStatus.uptime ? `Uptime: ${serverStatus.uptime}` : 'Uptime'}</span>
              <Info size={12} className={theme.text.secondary} />
            </button>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { if (!isLoaded) return; onLoginClick(); }}
            className={`flex items-center gap-1.5 ${theme.text.secondary} text-xs font-sans transition-colors hover:text-pink-400`}
          >
            {!isLoaded ? (
              <span className={`${theme.text.muted} text-xs font-sans`}>···</span>
            ) : user ? (
              <>
                {user.imageUrl
                  ? <img src={user.imageUrl} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0" />
                  : <User size={14} />}
                <span className="hidden sm:inline">{user.firstName || user.primaryEmailAddress?.emailAddress?.split('@')[0]}</span>
                <span className="sm:hidden">{user.firstName || user.primaryEmailAddress?.emailAddress?.split('@')[0]}</span>
              </>
            ) : (
              <>
                <LogIn size={14} />
                <span className="hidden sm:inline">Client Login</span>
                <span className="sm:hidden">Client Login</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Header Component
export const MainHeader = ({ 
  theme, 
  isDarkMode, 
  toggleTheme, 
  isMenuOpen, 
  menuClosing, 
  handleMenuToggle,
  handleServicesClick,
  handleProjectsClick,
  handleContactClick,
  showLogin,
  setShowLogin,
  setShowDashboard,
  onSignInSuccess,
  onSignOutSuccess,
  closeDashboard
}) => {
  const { user: menuUser } = useUser();
  const { signOut: menuSignOut } = useClerk();

  const [loginVisible, setLoginVisible] = useState(false);
  const [loginMounted, setLoginMounted] = useState(false);

  useEffect(() => {
    if (showLogin) {
      setLoginMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setLoginVisible(true)));
    } else {
      setLoginVisible(false);
      const t = setTimeout(() => setLoginMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [showLogin]);

  return (
    <>
      {loginMounted && (
        <LoginModal theme={theme} isDarkMode={isDarkMode} onClose={() => setShowLogin(false)} onSignInSuccess={onSignInSuccess} onSignOutSuccess={onSignOutSuccess} isVisible={loginVisible} />
      )}

      <header className={`sticky-header border-b ${theme.header} px-6 py-4 relative`}>
        <div className="flex items-center justify-between max-w-6xl mx-auto relative z-10">
          <div className="flex items-center space-x-3">
            <img 
              src={isDarkMode ? "/logo_dark_6.svg" : "/logo_light_6.svg"}
              alt="Spiral Creations" 
              className="h-10 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div 
              className={`w-10 h-10 ${isDarkMode ? 'bg-white' : 'bg-slate-800'} rounded-lg`}
              style={{ display: 'none', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}
            >
              <div className="w-6 h-6 bg-pink-600 rounded-md"></div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button 
              onClick={toggleTheme}
              className={`${isDarkMode ? 'text-white' : 'text-slate-900'} transition-colors duration-300 p-2 rounded-full ${isDarkMode ? 'bg-slate-800/60 hover:bg-slate-800/80 border border-slate-700/50' : 'bg-gray-100/60 hover:bg-gray-100/80 border border-gray-300'} backdrop-blur-sm`}
            >
              {isDarkMode ? (
                <Sun size={24} className="spin-animation" />
              ) : (
                <Moon size={24} className="spin-animation" />
              )}
            </button>
            
            <button 
              onClick={handleMenuToggle}
              className={`${isDarkMode ? 'text-white' : 'text-slate-900'} transition-colors duration-300 p-2 rounded-full ${isDarkMode ? 'bg-slate-800/60 hover:bg-slate-800/80 border border-slate-700/50' : 'bg-gray-100/60 hover:bg-gray-100/80 border border-gray-300'} backdrop-blur-sm`}
            >
              {(isMenuOpen || menuClosing) ? (
                <X size={24} className={menuClosing ? "spin-out-animation" : "spin-animation"} />
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className={`overflow-hidden transition-all duration-300 ease-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className={`mt-4 ${theme.menu} backdrop-blur-sm rounded-lg border p-4 max-w-6xl mx-auto shadow-xl ${isMenuOpen && !menuClosing ? 'menu-open' : menuClosing ? 'menu-closing' : ''}`}>
            <nav>
              <button 
                onClick={() => { if (closeDashboard) { closeDashboard(); setTimeout(() => handleProjectsClick(), 100); } else handleProjectsClick(); }}
                className={`flex items-center ${theme.text.secondary} py-2 px-3 rounded-lg ${theme.hoverCard} font-sans transition-colors w-full text-left`}
              >
                What We Do
              </button>
              <button 
                onClick={() => { if (closeDashboard) { closeDashboard(); setTimeout(() => handleContactClick(), 100); } else handleContactClick(); }}
                className={`flex items-center ${theme.text.secondary} py-2 px-3 rounded-lg ${theme.hoverCard} font-sans transition-colors w-full text-left`}
              >
                Contact
              </button>
              <div className={`border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-200'} pt-2 mt-2`}>
                {menuUser ? (
                  <div>
                    {/* User info header */}
                    <div className={`flex items-center space-x-2 py-2 px-3 font-sans`}>
                      {menuUser?.imageUrl ? (
                        <img src={menuUser.imageUrl} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 bg-gradient-to-br from-pink-500 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <User size={14} className="text-white" />
                        </div>
                      )}
                      <div className="flex flex-col justify-center min-w-0">
                        <p className={`text-sm font-semibold ${theme.text.primary} font-sans leading-tight truncate`}>
                          {menuUser.firstName ? `${menuUser.firstName} ${menuUser.lastName || ''}`.trim() : menuUser.primaryEmailAddress?.emailAddress}
                        </p>
                        {menuUser.firstName && <p className={`text-xs ${theme.text.muted} font-sans leading-tight truncate`}>{menuUser.primaryEmailAddress?.emailAddress}</p>}
                      </div>
                    </div>
                    {/* Indented sub-menu */}
                    <div className="flex">
                      <div className="w-7 flex-shrink-0 flex justify-center ml-3">
                        <div className="w-0.5 rounded-full" style={{ background: isDarkMode ? '#475569' : '#d1d5db' }} />
                      </div>
                      <div className="flex-1 mt-1 space-y-0.5">
                      <button
                        onClick={() => { setShowDashboard(true); handleMenuToggle(); }}
                        className={`flex items-center space-x-2 ${theme.text.secondary} py-1.5 pl-2 pr-3 rounded-lg ${theme.hoverCard} font-sans transition-colors w-full text-left text-sm`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isDarkMode ? '#9ca3af' : '#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                        <span>Dashboard</span>
                      </button>
                      <button
                        onClick={() => { handleMenuToggle(); menuSignOut().then(() => { if (window.__scSignOutToast) window.__scSignOutToast(); }); }}
                        className={`group flex items-center space-x-2 ${theme.text.secondary} hover:text-red-400 py-1.5 pl-2 pr-3 rounded-lg ${theme.hoverCard} font-sans transition-colors w-full text-left text-sm`}
                      >
                        <LogIn size={14} className="rotate-180 transition-colors text-gray-400 group-hover:text-red-400" />
                        <span>Sign Out</span>
                      </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { handleMenuToggle(); setShowLogin(true); }}
                    className={`flex items-center space-x-2 ${theme.text.secondary} py-2 px-3 rounded-lg ${theme.hoverCard} font-sans transition-colors w-full text-left`}
                  >
                    <LogIn size={18} className={theme.text.muted} />
                    <span>Client Login</span>
                  </button>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
};